import React from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  getDatabase,
  ref,
  get,
  update,
  serverTimestamp,
} from "firebase/database";
import { sendAcceptOfferMessageAfterPayment } from "./utils/sendAcceptOfferUtils";
// import { removeAbandonedBooking } from "./utils/bookingUtils";
import {
  getBookingByID,
  sendEmail,
  sendSMS,
  updateBookingStatus,
  checkCoupon,
  getInquiryDataById,
  getPlatformPercentages,
  getCurrentUserDetails,
  getUserByID,
  BookingFormEmailBody,
  getOwnerMeta,
  getUserIdByRefferralCode,
  getListingByID,
  getCommissionRateByReferralCode,
} from "./utils";
import { notifyOwnerBookingPaid } from "./utils/userNotifications";
import { notifyRenterPaymentConfirmed } from "./utils/userNotifications";
import { toPassengerCount } from "./utils/passengerCounts";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAuth } from "../contexts/AuthContext";
import { useFormContext } from "../contexts/FormContext";
import TextField from "@mui/material/TextField";
import { Alert, Checkbox, FormControlLabel } from "@mui/material";
import { Col, Row, Button, Modal } from "react-bootstrap";
import { ThreeDots } from "react-loader-spinner";

// Extend dayjs with customParseFormat plugin for parsing date strings
dayjs.extend(customParseFormat);

// Helper function to calculate renter fee (straight 5%, no cap)
// For fishing charters (boatCaptain === true): charge 5% platform fee as renter fee
// For regular rentals (boatCaptain === false): charge 5% renter fee
const calculateRenterFee = (amount, listingData = null) => {
  if (listingData?.boatCaptain === true) {
    // Fishing charter: charge 5% platform fee as renter fee
    return 0.05 * amount;
  }
  // Regular rental: charge 5% renter fee
  return 0.05 * amount; // 5% of amount (in cents)
};

// Advance bookings (>14 days): 20% rental deposit + full platform fee + full tax upfront;
// remaining 80% of rental is charged 48 hours before the trip.
const calculateAdvanceBookingAmounts = (
  rentalAmountCents,
  renterFeeCents,
  taxCents,
  discountCents = 0
) => {
  const rentalDepositCents = Math.round(0.2 * rentalAmountCents);
  const advanceChargeCents =
    rentalDepositCents + renterFeeCents + taxCents - discountCents;
  const remainingRentalCents = rentalAmountCents - rentalDepositCents;

  return {
    rentalDepositCents,
    advanceChargeCents,
    remainingRentalCents,
  };
};

const CHARTER_FULL_PAYMENT_HOURS = 48;

const getHoursUntilTrip = (offer, bookingData) => {
  const dateTimeCandidates = [];

  if (offer?.offeredDate && offer?.offeredTime) {
    dateTimeCandidates.push(`${offer.offeredDate} ${offer.offeredTime}`);
  }
  if (offer?.arivingDateTime) {
    dateTimeCandidates.push(offer.arivingDateTime);
  }
  if (bookingData?.tripDate && bookingData?.tripTime) {
    dateTimeCandidates.push(`${bookingData.tripDate} ${bookingData.tripTime}`);
  }

  for (const dateTimeString of dateTimeCandidates) {
    const parsed = dayjs(dateTimeString);
    if (parsed.isValid()) {
      return (parsed.toDate() - new Date()) / (1000 * 60 * 60);
    }
  }

  return Infinity;
};

const shouldUseCharterDeposit = (hasCharterDeposit, hoursUntilTrip) =>
  hasCharterDeposit && hoursUntilTrip > CHARTER_FULL_PAYMENT_HOURS;

const BOOKING_ALREADY_PAID_STATUSES = [
  "waiver pending",
  "waiver filled",
  "paid",
  "tripstarted",
  "completed",
  "trip completed",
  "trip returned",
  "trip concluded",
];

const isBookingAlreadyPaid = (status) =>
  BOOKING_ALREADY_PAID_STATUSES.includes(String(status || "").toLowerCase());

// Empty payment stubs are created before checkout. Only treat as paid if Stripe already ran.
const hasCompletedStripeCharge = (payment) =>
  Boolean(payment?.paidAt || payment?.stripePaymentIntentId || payment?.paymentIntentId);

// Helper function to determine if a rental is taxable
// Tax (8.25%) is ONLY collected for rentals in Texas that are driven by the renter (no captain).
// No tax for: Texas charters/captain-driven rentals, or ANY rental outside of Texas.
const checkIfRentalIsTaxable = async (listingData) => {
  try {
    // boatCaptain === true means only owner/captain drives (charter) => NOT taxable
    // boatCaptain === false means renter can drive => potentially taxable (if in Texas)
    if (listingData?.boatCaptain === true) {
      console.log("Tax check: Captain/charter rental - not taxable");
      return false;
    }

    // Check if listing is in Texas using reverse geocoding
    const lat = listingData?.storageAddress?.lat;
    const lng = listingData?.storageAddress?.lng;
    if (!lat || !lng) {
      console.log("Tax check: No location data - not taxable");
      return false;
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBfEcoyW9DM7QQWdV3oTjevrfyhX5n5qqg`
    );
    const data = await response.json();
    const addressComponents = data.results[0]?.address_components || [];
    const stateComponent = addressComponents.find((component) =>
      component.types.includes("administrative_area_level_1")
    );
    const state = stateComponent?.short_name || "";
    console.log("Tax check: State =", state, "| boatCaptain =", listingData?.boatCaptain);

    // Only taxable if in Texas (TX) and renter-driven (boatCaptain is false)
    return state === "TX";
  } catch (error) {
    console.error("Error checking tax eligibility:", error);
    return false; // Default to not taxable on error
  }
};

const PaymentForm = ({ amount }) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL; // Fetch the base URL from .env
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate(); // Initialize useNavigate
  const auth = useAuth();
  const { setIsFormDirty, setFormType, setFormMessage } = useFormContext();

  const [storedBookingId, setBookingId] = useState("");
  const [storedPaymentId, setPaymentId] = useState("");
  const [bookingData, setBookingData] = useState("");
  const [acknowledgement2, setAcknowledgement2] = useState(false);
  const [acknowledgementBassBoat, setAcknowledgementBassBoat] = useState(false);
  const [offer, setOfferData] = useState("");
  const [inquiryData, setInquiryData] = useState([]);

  const [totalTax, setTotalTax] = useState(0);
  const [adminTax, setAdminTax] = useState(0);
  const [amountTotal, setAmountTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [coupon, setCouponCode] = useState("");
  const [isTaxable, setIsTaxable] = useState(false);
  const [isPayButtonDisabled, setIsPayButtonDisabled] = useState(false);
  const [deadlineExpired, setDeadlineExpired] = useState(false);
  const [deadlineMessage, setDeadlineMessage] = useState("");

  // Function to set payment deadline when user leaves without completing payment
  // Instead of cancelling immediately, give the renter 24 hours to complete payment
  const handleSetPaymentDeadline = async (bookingId) => {
    console.log("Setting payment deadline for booking ID:", bookingId);
    try {
      const database = getDatabase();

      // Get booking data
      const bookingRef = ref(database, `bookings/${bookingId}`);
      const bookingSnapshot = await get(bookingRef);
      const bookingData = bookingSnapshot.val();

      if (!bookingData) {
        console.error(`No booking found with ID ${bookingId}`);
        return;
      }

      // Don't modify if already paid, cancelled, or past payment (e.g. Waiver Pending)
      if (
        isBookingAlreadyPaid(bookingData.status) ||
        bookingData.status === "cancelled"
      ) {
        console.log(`Booking ${bookingId} is already ${bookingData.status}, skipping deadline update`);
        return;
      }

      // If no deadline exists yet, set one for 24 hours from now
      if (!bookingData.paymentDeadline) {
        const deadline = new Date();
        deadline.setHours(deadline.getHours() + 24);
        
        await update(bookingRef, {
          status: "pending",
          paymentDeadline: deadline.toISOString(),
          paymentInProgress: false,
        });
        
        console.log(`Set 24-hour payment deadline for booking ${bookingId}: ${deadline.toISOString()}`);
      } else {
        // Deadline already exists, just remove the in-progress flag
        await update(bookingRef, {
          paymentInProgress: false,
        });
        console.log(`Booking ${bookingId} already has a payment deadline, removed in-progress flag`);
      }
    } catch (error) {
      console.error("Error setting payment deadline:", error);
    }
  };

  const [discount, setDiscount] = useState(0);
  const [totalOwnerBookings, setTotalOwnerBookings] = useState(0);

  const queryParameters = new URLSearchParams(window.location.search);
  const bookingId = queryParameters.get("bookingId");
  const [platformPercentages, setPlatformPercentages] = useState({});

  const handleAcknowledgement = (acknowledgementName) => {
    if (acknowledgementName === "acknowledgement1") {
    } else if (acknowledgementName === "acknowledgement2") {
      setAcknowledgement2((prev) => !prev);
    }
  };

  const [listingData, setListingData] = useState([]);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Set form as dirty to show navigation warning when user tries to leave payment page
  useEffect(() => {
    setIsFormDirty(true);
    setFormType('payment');
    setFormMessage('You have a pending payment. You can complete this payment within 24 hours, otherwise the booking will be cancelled. Do you want to leave this page?');

    // Add beforeunload listener to warn on browser close/refresh
    const handleBeforeUnload = (e) => {
      if (shouldBlockNavigation) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Clear form dirty state when leaving the payment page
      setIsFormDirty(false);
      setFormType('');
      setFormMessage('');
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setIsFormDirty, setFormType, setFormMessage, shouldBlockNavigation]);

  // When component mounts, mark the booking as in-progress to prevent automatic deletion
  useEffect(() => {
    const markBookingInProgress = async () => {
      if (bookingId) {
        try {
          // First check if this booking is already paid
          const completedPayments = JSON.parse(
            localStorage.getItem("completedPayments") || "[]"
          );
          if (completedPayments.includes(bookingId)) {
            console.log(`Payment already completed for booking ${bookingId}`);
            return; // Already paid, no need to mark as in-progress
          }

          // Mark the booking as in-progress in the database
          const database = getDatabase();
          const bookingRef = ref(database, `bookings/${bookingId}`);
          const bookingSnapshot = await get(bookingRef);

          if (bookingSnapshot.exists()) {
            const bookingData = bookingSnapshot.val();
            // Only update if it's not already paid
            if (!isBookingAlreadyPaid(bookingData.status)) {
              await update(bookingRef, { paymentInProgress: true });
              console.log(`Marked booking ${bookingId} as payment in progress`);

              // Store the booking ID in localStorage to clean up later if needed
              localStorage.setItem("currentPaymentBookingId", bookingId);
            }
          }
        } catch (error) {
          console.error("Error marking booking as in-progress:", error);
        }
      }
    };

    markBookingInProgress();

    // Cleanup function - set payment deadline if payment wasn't completed
    // Renter has 24 hours to complete payment instead of immediate cancellation
    return () => {
      const cleanupBooking = async () => {
        const currentBookingId = localStorage.getItem(
          "currentPaymentBookingId"
        );
        if (currentBookingId && currentBookingId === bookingId) {
          // Check if this booking was paid
          const completedPayments = JSON.parse(
            localStorage.getItem("completedPayments") || "[]"
          );
          if (!completedPayments.includes(bookingId)) {
            console.log(
              `User left payment page without completing payment for booking ${bookingId}`
            );
            // Set 24-hour payment deadline instead of cancelling immediately
            await handleSetPaymentDeadline(bookingId);
          }
          localStorage.removeItem("currentPaymentBookingId");
        }
      };

      cleanupBooking();
    };
  }, [bookingId]);

  useEffect(() => {
    // Open the payment modal when component mounts
    setShowPaymentModal(true);

    const fetchDetails = async () => {
      try {
        const bookingDetails = await getBookingByID(bookingId);
        if (!bookingDetails) {
          // No need to proceed if bookingDetails is null
          return;
        }

        // Check if booking is already cancelled
        if (bookingDetails.status === "cancelled") {
          setDeadlineExpired(true);
          setDeadlineMessage("This booking has been cancelled.");
          return;
        }

        // Block a second card charge if this booking already completed payment.
        // Fishing charters can look "unpaid" again (status left as pending, or
        // Pay Now reopened within 48h of the trip) and would otherwise charge
        // the full amount on top of deposit + remaining.
        if (isBookingAlreadyPaid(bookingDetails.status)) {
          setDeadlineExpired(true);
          setDeadlineMessage(
            "Payment for this booking has already been completed. You will not be charged again."
          );
          setShouldBlockNavigation(false);
          return;
        }

        if (bookingDetails.paymentId) {
          const existingPaymentSnap = await get(
            ref(getDatabase(), `payments/${bookingDetails.paymentId}`)
          );
          const existingPayment = existingPaymentSnap.val();
          if (hasCompletedStripeCharge(existingPayment)) {
            setDeadlineExpired(true);
            setDeadlineMessage(
              "Payment for this booking has already been completed. You will not be charged again."
            );
            setShouldBlockNavigation(false);
            return;
          }
        }

        // Check if payment deadline has expired (24-hour window)
        if (bookingDetails.paymentDeadline) {
          const deadline = new Date(bookingDetails.paymentDeadline);
          const now = new Date();
          
          if (now > deadline) {
            // Deadline has expired - cancel the booking
            console.log(`Payment deadline expired for booking ${bookingId}`);
            await updateBookingStatus(bookingId, "cancelled");
            setDeadlineExpired(true);
            setDeadlineMessage("The 24-hour payment window has expired. This booking has been cancelled. Please create a new booking to proceed.");
            return;
          } else {
            // Show remaining time to user
            const hoursLeft = Math.ceil((deadline - now) / (1000 * 60 * 60));
            const minutesLeft = Math.ceil((deadline - now) / (1000 * 60)) % 60;
            if (hoursLeft > 0) {
              setDeadlineMessage(`You have ${hoursLeft} hour${hoursLeft > 1 ? 's' : ''} remaining to complete this payment.`);
            } else {
              setDeadlineMessage(`You have ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''} remaining to complete this payment.`);
            }
          }
        }

        const listingDetails = await getListingByID(bookingDetails.listingId);
        setListingData(listingDetails);

        const ownerMetaDetails = await getOwnerMeta(bookingDetails.owner);

        if (ownerMetaDetails?.totalPaidBookings != null) {
          setTotalOwnerBookings(ownerMetaDetails.totalPaidBookings);
        } else {
          setTotalOwnerBookings(0);
        }

        // Check if this is an instant booking (no inquiryId)
        const isInstantBooking = bookingDetails.instantBook || !bookingDetails.inquiryId;

        let inquiryDetails = null;
        let offerDetails = null;

        if (isInstantBooking) {
          // For instant bookings, create offer-like data from booking details
          const duration = bookingDetails.duration || {};
          let durationText = '';

          if (duration.overnight > 0) {
            durationText = `${duration.overnight} Night${duration.overnight > 1 ? 's' : ''}`;
          } else if (duration.sameDayHours > 0) {
            durationText = `${duration.sameDayHours} Hour${duration.sameDayHours > 1 ? 's' : ''}`;
            if (duration.sameDayMinutes > 0) {
              durationText += ` ${duration.sameDayMinutes} Minute${duration.sameDayMinutes > 1 ? 's' : ''}`;
            }
          }

          offerDetails = {
            offerPrice: bookingDetails.priceOffered,
            offeredDate: bookingDetails.tripDate,
            offeredTime: bookingDetails.tripTime,
            duration: durationText,
            arivingDateTime: `${bookingDetails.tripDate} ${bookingDetails.tripTime}`,
          };

          // Create inquiry-like data from booking details
          inquiryDetails = {
            inquiryData: bookingDetails.passengers || {},
            offerData: offerDetails,
          };
        } else {
          // Traditional flow: fetch inquiry data
          inquiryDetails = await getInquiryDataById(bookingDetails["inquiryId"]);
          offerDetails = inquiryDetails["offerData"];
        }

        setInquiryData(inquiryDetails);

        const platformFeesData = await getPlatformPercentages();

        // Check if this rental is taxable (only Texas rentals driven by renter)
        const taxable = await checkIfRentalIsTaxable(listingDetails);
        setIsTaxable(taxable);

        let platformData = {
          salesTax: taxable ? parseFloat(platformFeesData.defaultSalesTax) : 0,
        };

        if (listingDetails.boatType === "Bass Boat") {
          platformData.adminCut = parseFloat(platformFeesData.defaultAdminCut);
        } else {
          platformData.adminCut = parseFloat(
            platformFeesData.defaultOtherBoatTypeAdminCut
          );
        }

        console.log("Tax eligibility:", taxable ? "TAXABLE (Texas, renter-driven)" : "NOT TAXABLE");
        setPlatformPercentages(platformData);
        setOfferData(offerDetails);
        setBookingId(bookingId);
        setBookingData(bookingDetails);
        setPaymentId(bookingDetails.paymentId);
        console.log(platformPercentages, "test test");
      } catch (error) {
        console.error("Error fetching listing details:", error);
      }
    };

    fetchDetails();
  }, [bookingId]);

  const verifyDiscount = async (code) => {
    const coupon = await checkCoupon(code);
    if (coupon != null) {
      setDiscount(parseInt(coupon[0].discount * 100));
    }
  };

  const [error, setError] = useState("");

  // Updated handleSubmit function with new payment rules
  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setIsPayButtonDisabled(true);
    if (!acknowledgement2) {
      setError("You must acknowledge to continue.");
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }
    if (listingData?.boatType === "Bass Boat" && !acknowledgementBassBoat) {
      setError("You must acknowledge the Bass Boat motor hours policy to continue.");
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }

    // Stripe requires a minimum charge amount (e.g. $0.50 USD => 50 cents).
    // `amount` is expected to be in cents.
    if (!Number.isFinite(amount) || amount < 50) {
      setError("Payment amount is too low or invalid. Please refresh and try again.");
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }

    if (!stripe || !elements) {
      setError("Payment form is still loading. Please try again.");
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }

    if (!storedPaymentId) {
      setError("Booking payment details are still loading. Please refresh and try again.");
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    }

    try {
      const latestBooking = await getBookingByID(storedBookingId || bookingId);
      if (latestBooking && isBookingAlreadyPaid(latestBooking.status)) {
        setShouldBlockNavigation(false);
        navigate("/dashboard/thank-you", {
          state: { bookingId: storedBookingId || bookingId },
        });
        return;
      }
      const paymentIdToCheck = storedPaymentId || latestBooking?.paymentId;
      if (paymentIdToCheck) {
        const existingPaymentSnap = await get(
          ref(getDatabase(), `payments/${paymentIdToCheck}`)
        );
        const existingPayment = existingPaymentSnap.val();
        if (hasCompletedStripeCharge(existingPayment)) {
          await updateBookingStatus(
            storedBookingId || bookingId,
            "Waiver Pending"
          );
          setShouldBlockNavigation(false);
          navigate("/dashboard/thank-you", {
            state: { bookingId: storedBookingId || bookingId },
          });
          return;
        }
      }
    } catch (e) {
      console.warn("Could not verify existing payment before charge:", e);
    }

    // Create token from card element
    const { token, error } = await stripe.createToken(
      elements.getElement(CardElement)
    );

    if (error) {
      console.error(error);
      setError(
        error.message ||
        "There was an error processing your card. Please try again."
      );
      setLoading(false);
      setIsPayButtonDisabled(false);
      return;
    } else {
      // Calculate tax and fees
      const taxTotal = platformPercentages.salesTax * amount;
      setTotalTax(taxTotal);
      const taxAdmin = platformPercentages.adminCut * taxTotal;
      setAdminTax(taxAdmin);

      // Your existing fee calculation code
      let cutAdmin = 0;
      let refferalAmount = 0;
      let adminFirstTimeCut = 0;
      let cutPercentage = 0.1;
      const ownerData = await getUserByID(bookingData.owner);
      const referId = await getUserIdByRefferralCode(ownerData.refferalCode);

      // Get the commission rate based on owner's referral code
      // Special codes like "START@5" have custom rates (e.g., 5% instead of 10%)
      const ownerCommissionRate = await getCommissionRateByReferralCode(ownerData.refferalCode);
      cutPercentage = ownerCommissionRate;
      console.log(`Using commission rate: ${ownerCommissionRate * 100}% for referral code: ${ownerData.refferalCode}`);

      // FEATURE 2: First-Time Booking Rule
      if (totalOwnerBookings === 0) {
        if (amount < 20000) {
          // For bookings under $200, take 50% of the booking amount
          cutAdmin = calculateRenterFee(amount, listingData) + 0.5 * amount;
          // No fixed admin fee for bookings under $200
          adminFirstTimeCut = 0;
        } else if (referId) {
          // Bookings $200+ referred by a pro-staff member (referral code with userId)
          if (differenceInDays > 14) {
            cutAdmin =
              calculateRenterFee(amount, listingData) + ownerCommissionRate * 20000 + 4000;
          } else {
            cutAdmin =
              calculateRenterFee(amount, listingData) + ownerCommissionRate * amount + 4000;
          }
          adminFirstTimeCut = 4000;

          // Your existing referral logic
          const database = getDatabase();
          const referPayments = ref(database, `ownerMeta/${referId}`);

          const previousReferPaymentsSnapshot = await get(referPayments);

          if (previousReferPaymentsSnapshot.exists()) {
            const referPaymentData = previousReferPaymentsSnapshot.val();

            const previousCurrentAmount =
              referPaymentData.payments.currentAmount;
            const previousTotalAmount = referPaymentData.payments.totalAmount;
            const referPaymentObject = {
              currentAmount: previousCurrentAmount + 3000,
              totalAmount: previousTotalAmount + 3000,
            };
            await update(referPayments, { payments: referPaymentObject });
            refferalAmount = 3000;
          }
        } else {
          // Original logic for bookings $200 and above without referral
          if (differenceInDays > 14) {
            cutAdmin =
              calculateRenterFee(amount, listingData) + ownerCommissionRate * 20000 + 10000;
          } else {
            cutAdmin =
              calculateRenterFee(amount, listingData) + ownerCommissionRate * amount + 10000;
          }
          adminFirstTimeCut = 10000;
        }
      } else {
        // Non-first time owner logic - use the custom commission rate
        if (differenceInDays > 14) {
          cutAdmin = calculateRenterFee(amount, listingData) + ownerCommissionRate * 20000;
        } else {
          cutAdmin = calculateRenterFee(amount, listingData) + ownerCommissionRate * amount;
        }
      }

      const finalAmount =
        parseInt(amount + taxTotal) +
        calculateRenterFee(amount, listingData) -
        (discount > 0 ? discount : 0);
      setAmountTotal(finalAmount);
      const renterData = await getCurrentUserDetails();

      // Charter deposit percentage logic (in cents)
      // When a boat owner sets a deposit percentage, only that % is charged online initially
      // Balance collection depends on collectBalanceAtMeetup flag:
      //   - true: rest is paid in cash to the boat owner at meet-up
      //   - false (default): BBR collects the remaining balance 48 hours prior to the trip
      const charterDepositPercentage = listingData?.fishingGuideDetails?.policies?.paymentTerms?.depositPercentage
        ? parseFloat(listingData.fishingGuideDetails.policies.paymentTerms.depositPercentage)
        : null;
      const hasCharterDepositConfigured =
        charterDepositPercentage !== null &&
        charterDepositPercentage > 0 &&
        charterDepositPercentage < 100;
      const hoursUntilTrip = getHoursUntilTrip(offer, bookingData);
      const isCharterDeposit = shouldUseCharterDeposit(
        hasCharterDepositConfigured,
        hoursUntilTrip
      );
      const collectBalanceAtMeetup = listingData?.fishingGuideDetails?.policies?.paymentTerms?.collectBalanceAtMeetup === true;
      // For charter deposits: apply deposit % to base+tax only, then add full renter fee on top
      const baseAndTax = parseInt(amount + taxTotal) - (discount > 0 ? discount : 0);
      const charterChargeAmount = isCharterDeposit
        ? Math.round((charterDepositPercentage / 100) * baseAndTax) + calculateRenterFee(amount, listingData)
        : finalAmount;
      const charterRemainingAmount = isCharterDeposit ? finalAmount - charterChargeAmount : 0;

      // Console logs for charged amounts (in cents and dollars)
      try {
        const platformFeeCents = calculateRenterFee(amount, listingData);
        const totalCents = finalAmount;
        console.log("PaymentForm charge amounts", {
          baseAmountCents: amount,
          platformFeeCents: platformFeeCents,
          taxCents: taxTotal,
          totalCents: totalCents,
          baseAmount: Number((amount / 100).toFixed(2)),
          platformFee: Number((platformFeeCents / 100).toFixed(2)),
          tax: Number((taxTotal / 100).toFixed(2)),
          total: Number((totalCents / 100).toFixed(2)),
          charterDepositPercentage: charterDepositPercentage,
          charterChargeAmount: isCharterDeposit ? Number((charterChargeAmount / 100).toFixed(2)) : null,
          cashBalance: isCharterDeposit ? Number(((finalAmount - charterChargeAmount) / 100).toFixed(2)) : null,
        });
      } catch (e) {
        console.warn("PaymentForm charge amount logging failed", e);
      }

      try {
        // FEATURE 1: Upfront Payment Rule
        // Determine if we should charge the full amount upfront
        const isLowValueFutureBooking = amount < 20000 && differenceInDays > 14;

        // Check if this is an instant booking (always full payment)
        const isInstantBookingPayment = bookingData?.instantBook || bookingData?.packageBooking;

        // Choose the appropriate endpoint based on booking distance
        // Instant bookings always use the regular capture-payment endpoint (full payment)
        // Charter deposits with collectBalanceAtMeetup use capture-payment (deposit only, rest in cash)
        // Charter deposits WITHOUT collectBalanceAtMeetup use capture-payment-advance (BBR collects remaining 48hrs prior)
        let endpoint;
        if (isCharterDeposit && !collectBalanceAtMeetup) {
          // Charter deposit where BBR collects remaining balance 48 hours prior
          endpoint = `${BASE_URL}/capture-payment-advance`;
        } else if (isCharterDeposit || isInstantBookingPayment || differenceInDays <= 14) {
          endpoint = `${BASE_URL}/capture-payment`;
        } else {
          endpoint = `${BASE_URL}/capture-payment-advance`;
        }

        const renterFeeCents = calculateRenterFee(amount, listingData);
        const isAdvanceDepositBooking =
          differenceInDays > 14 &&
          !isLowValueFutureBooking &&
          !isInstantBookingPayment &&
          !isCharterDeposit;
        const advanceBookingAmounts = isAdvanceDepositBooking
          ? calculateAdvanceBookingAmounts(
              amount,
              renterFeeCents,
              taxTotal,
              discount > 0 ? discount : 0
            )
          : null;

        // For charter deposits, charge only the deposit percentage
        let chargeAmount = isCharterDeposit ? charterChargeAmount : finalAmount;
        if (isAdvanceDepositBooking) {
          chargeAmount = advanceBookingAmounts.advanceChargeCents;
        }

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "Access-Control-Allow-Origin": `${BASE_URL}`,
          },
          mode: "cors",
          body: JSON.stringify({
            tokenId: token.id,
            amount: finalAmount,
            advanceChargeAmount: isAdvanceDepositBooking
              ? advanceBookingAmounts.advanceChargeCents
              : isCharterDeposit && !collectBalanceAtMeetup
                ? charterChargeAmount
                : undefined,
            remainingChargeAmount: isAdvanceDepositBooking
              ? advanceBookingAmounts.remainingRentalCents
              : isCharterDeposit && !collectBalanceAtMeetup
                ? charterRemainingAmount
                : undefined,
            daysDifference: differenceInDays,
            paymentId: storedPaymentId,
            bookingId: storedBookingId || bookingId,
            renter: bookingData.renter,
            owner: bookingData.owner,
            email: renterData.email,
            title: bookingData.listingTitle,
            isLowValueFutureBooking, // Flag for low-value future bookings
            firstRentalPayment: totalOwnerBookings === 0 ? 1 : 0,
            adminFirstTimeCut: adminFirstTimeCut || 0,
            firstTimeSpecialRate: totalOwnerBookings === 0 && amount < 20000,
            tripDate: (() => {
              const raw =
                bookingData?.tripDate && bookingData?.tripTime
                  ? `${bookingData.tripDate} ${bookingData.tripTime}`
                  : offer?.offeredDate && offer?.offeredTime
                    ? `${offer.offeredDate} ${offer.offeredTime}`
                    : offer?.arivingDateTime || null;
              if (!raw) return null;
              const cleaned = String(raw).replace(/\s+at\s+/i, " ");
              const parsed = formatDateTime(cleaned);
              return parsed instanceof Date && !Number.isNaN(parsed.getTime())
                ? parsed.toISOString()
                : cleaned;
            })(),
          }),
        });

        const result = await response.json();
        console.log("result: ", result);

        if (result.success) {
          // Update the booking status to "Waiver Pending"
          console.log("Payment successful, updating booking status for:", storedBookingId);
          await updateBookingStatus(storedBookingId, "Waiver Pending");
          console.log("Booking status updated to Waiver Pending");

          const database = getDatabase();
          const paymentRef = ref(database, `payments/${storedPaymentId}`);

          let dbOwnerAmount = 0;
          // Rental deposit portion for advance bookings (20% of rental amount, in cents)
          const depositAmount =
            advanceBookingAmounts?.rentalDepositCents ?? Math.round(0.2 * amount);

          // Calculate owner amount based on booking type
          // Instant bookings always get full payment treatment
          if (isCharterDeposit) {
            // Charter deposit: owner gets the deposit portion minus the admin % on the deposit only.
            // charterChargeAmount = (depositPct% × base) + renterFee
            // depositPortion = charterChargeAmount - renterFee
            // adminCutOnDeposit = platformPercentages.adminCut × depositPortion
            const renterFee = calculateRenterFee(amount, listingData);
            const depositPortion = charterChargeAmount - renterFee;
            const adminCutOnDeposit = Math.round(cutPercentage * depositPortion);
            dbOwnerAmount = depositPortion - adminCutOnDeposit;
          } else if (differenceInDays > 14 && !isLowValueFutureBooking && !isInstantBookingPayment) {
            // Deposit-only future booking, including first rentals. The $100
            // first-rental fee is included in cutAdmin so ownerAmount is net of it.
            dbOwnerAmount = Math.max(
              0,
              depositAmount - cutAdmin + calculateRenterFee(amount, listingData)
            );
          } else {
            // Near-term booking OR low-value future booking OR instant booking (full payment)
            dbOwnerAmount = amount - cutAdmin + calculateRenterFee(amount, listingData);
          }

          try {
            // Store payment details
            // Instant bookings always pay full amount
            let paymentData = {
              totalAmount: finalAmount,
              paidAmount: isCharterDeposit
                ? charterChargeAmount
                : isAdvanceDepositBooking
                  ? advanceBookingAmounts.advanceChargeCents
                  : finalAmount,
              ownerAmount: dbOwnerAmount,
              discount: discount > 0 ? discount : 0,
              cutPercentage,
              totalTax: taxTotal,
              adminCut: cutAdmin,
              adminTax: taxAdmin,
              dateTime: dayjs().format("dddd, MMMM D, YYYY HH:mm:ss"),
              transactionId: result.chargeId || result.paymentIntent?.id || result.paymentIntentId || "0",
              userId: auth.currentUser.uid,
              bookingId: storedBookingId,
              owner: bookingData.owner,
              status: "paid", // Payment status
              payout_status: "pending", // Payout status
              paidAt: new Date().toISOString(),
            };

            // Charter deposit fields
            if (isCharterDeposit) {
              paymentData.charterDeposit = true;
              paymentData.charterDepositPercentage = charterDepositPercentage;
              paymentData.charterDepositPaid = charterChargeAmount;
              paymentData.charterRemainingBalance = charterRemainingAmount;
              paymentData.collectBalanceAtMeetup = collectBalanceAtMeetup;
              if (collectBalanceAtMeetup) {
                // Balance to be paid in cash at meet-up
                paymentData.charterCashBalance = charterRemainingAmount;
                paymentData.cashBalanceDueToOwner = true;
              } else {
                // BBR collects remaining balance 48 hours prior to trip
                paymentData.charterCashBalance = 0;
                paymentData.cashBalanceDueToOwner = false;
                paymentData.advanceBooking = 1;
                paymentData.advancePaid = charterChargeAmount;
                paymentData.remainingAmount = charterRemainingAmount;
              }
            }

            // Additional fields for advance bookings with partial payment
            // Instant bookings skip advance booking logic
            // Charter deposits are handled above (with or without advance booking)
            if (isCharterDeposit) {
              // Already handled in charter deposit fields above
            } else if (isAdvanceDepositBooking) {
              paymentData.advanceBooking = 1;
              paymentData.advancePaid = advanceBookingAmounts.advanceChargeCents;
              paymentData.rentalDepositAmount = advanceBookingAmounts.rentalDepositCents;
              paymentData.remainingAmount = advanceBookingAmounts.remainingRentalCents;
              paymentData.advanceFeesUpfront = true;
            } else if (isInstantBookingPayment) {
              // Mark as instant booking with full payment
              paymentData.instantBooking = true;
              paymentData.fullPaymentUpfront = true;
            } else if (isLowValueFutureBooking) {
              // For low-value future bookings, mark as advance booking but with full payment
              paymentData.advanceBooking = 1;
              paymentData.advancePaid = finalAmount;
              paymentData.remainingAmount = 0;
              paymentData.isLowValueFullPayment = true;
            }

            if (totalOwnerBookings === 0) {
              paymentData.firstRentalPayment = 1;
              paymentData.adminFirstTimeCut = adminFirstTimeCut;

              // Add a flag for the special first-time low-value case
              if (amount < 20000) {
                paymentData.firstTimeSpecialRate = true;
                paymentData.specialAdminRate = 0.5; // 50% admin fee
              }
            }

            if (refferalAmount > 0) {
              paymentData.referrer = referId;
              paymentData.referAmount = refferalAmount;
            }

            await update(paymentRef, paymentData);

            // Handle owner payment records
            // const ownerMetaPayments = ref(
            //   database,
            //   `ownerMeta/${bookingData.owner}`
            // );
            // const previousOwnerPaymentsSnapshot = await get(ownerMetaPayments);

            // if (previousOwnerPaymentsSnapshot.exists()) {
            //   const previousOwnerPayments = previousOwnerPaymentsSnapshot.val();
            //   const previousCurrentAmount =
            //     previousOwnerPayments.payments.currentAmount;
            //   const previousTotalAmount =
            //     previousOwnerPayments.payments.totalAmount;

            //   const ownerPayments = {
            //     currentAmount: previousCurrentAmount + dbOwnerAmount,
            //     totalAmount: previousTotalAmount + dbOwnerAmount,
            //   };

            //   console.log(ownerPayments);

            //   if (previousOwnerPayments.previousTotalAmount) {
            //     const updatedTotalPaidBookings =
            //       parseInt(previousOwnerPayments.previousTotalAmount) + 1;
            //     await update(ownerMetaPayments, {
            //       payments: ownerPayments,
            //       totalPaidBookings: updatedTotalPaidBookings,
            //     });
            //   } else {
            //     await update(ownerMetaPayments, {
            //       payments: ownerPayments,
            //       totalPaidBookings: 1,
            //     });
            //   }
            // } else {
            //   const ownerPayments = {
            //     currentAmount: dbOwnerAmount,
            //     totalAmount: dbOwnerAmount,
            //   };
            //   console.log(ownerPayments);
            //   await update(ownerMetaPayments, {
            //     payments: ownerPayments,
            //     totalPaidBookings: 1,
            //   });
            // }

            // Send notifications
            const subjectEmail = "Booking Request Submitted";
            const renterName = renterData.firstName + " " + renterData.lastName;
            const emailBody = await BookingFormEmailBody(
              renterName,
              bookingData.listingTitle,
              "has been paid."
            );
            sendEmail(renterData.email, subjectEmail, emailBody);

            const renterSmsBody = `Hello ${renterName}, Payment for ${bookingData.listingTitle} received. Thank you!`;
            const renterSmsResponse = sendSMS(renterData.phone, renterSmsBody);
            console.log(renterSmsResponse);

            const ownerName = ownerData.firstName + " " + ownerData.lastName;
            const subjectOwnerEmail = "Booking Payment Confirmed";
            const emailOwnerBody = await BookingFormEmailBody(
              ownerName,
              bookingData.listingTitle,
              "received a new booking payment."
            );
            sendEmail(ownerData.email, subjectOwnerEmail, emailOwnerBody);

            const ownerSmsBody = `Hello ${ownerName}, payment for ${bookingData.listingTitle} received from ${renterName}.`;
            const ownerSmsResponse = sendSMS(ownerData.phone, ownerSmsBody);
            console.log(ownerSmsResponse);

            notifyOwnerBookingPaid(
              bookingData.owner,
              bookingData.listingTitle,
              storedBookingId
            ).catch((err) =>
              console.error("Failed to create owner notification:", err)
            );
            notifyRenterPaymentConfirmed(
              bookingData.renter,
              bookingData.listingTitle,
              storedBookingId
            ).catch((err) =>
              console.error("Failed to create renter notification:", err)
            );

            // Check if this payment is for an accepted offer and send the accept offer message
            try {
              const acceptOfferDataString =
                localStorage.getItem("acceptOfferData");
              if (acceptOfferDataString) {
                await sendAcceptOfferMessageAfterPayment();
                console.log("Accept offer message sent after payment");
              }
            } catch (error) {
              console.error("Error sending accept offer message:", error);
              // Continue with navigation even if there's an error with the accept offer message
            }

            // Store completed payment in localStorage to persist across page refreshes/navigations
            const completedPayments = JSON.parse(
              localStorage.getItem("completedPayments") || "[]"
            );
            if (!completedPayments.includes(storedBookingId)) {
              completedPayments.push(storedBookingId);
              localStorage.setItem(
                "completedPayments",
                JSON.stringify(completedPayments)
              );
            }

            // Remove the in-progress flag since payment is complete
            localStorage.removeItem("currentPaymentBookingId");

            // Disable navigation blocking since payment is complete
            setShouldBlockNavigation(false);

            navigate("/dashboard/thank-you", {
              state: { bookingId: storedBookingId },
            });
            setLoading(false);
          } catch (error) {
            console.log("Error saving data: ", error);
            // Stripe already succeeded. Do not re-enable Pay or show a charge error.
            setShouldBlockNavigation(false);
            navigate("/dashboard/thank-you", {
              state: { bookingId: storedBookingId },
            });
            setLoading(false);
          }
        } else if (result.error) {
          console.error("Payment failed:", result.error);
          setError(result.error);
          setLoading(false);
          setIsPayButtonDisabled(false);
        } else {
          console.error("Payment failed:");
          setError("Payment failed: ");
          setLoading(false);
          setIsPayButtonDisabled(false);
        }
      } catch (error) {
        console.error("Error capturing payment:", error);
        setError(
          error?.message ||
            "There was an error processing your payment. If you were charged, do not try again — contact support."
        );
        setLoading(false);
        setIsPayButtonDisabled(false);
      }
    }
  };

  const holdPayment = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement(CardElement) ?? {},
    });

    if (error) {
      console.error(error);
    } else {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/payment-hold`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              amount: 80000,
              payment_method: paymentMethod.id,
            }),
          }
        );

        const result = await response.json();
        console.log("result: ", result);

        const database = getDatabase();
        const paymentRef = ref(database, `payments/${storedPaymentId}`);

        try {
          // Retrieve existing data
          const currentData = (await get(paymentRef)).val() || {};

          const paymentHeld = {
            id: result.paymentIntent.id,
            amount: result.paymentIntent.amount,
            status: "pending",
          };

          // Merge existing data with new data
          const mergedData = {
            ...currentData,
            paymentHeld: paymentHeld,
          };

          // Update the database with merged data
          await update(paymentRef, mergedData);
        } catch (error) {
          console.log("Error saving data: ", error.message);
        }
      } catch (error) {
        console.error("Error capturing payment:", error);
      }
    }
  };

  const options = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };
  function formatDateTime(dateTimeString) {
    // Try parsing with specific formats for instant booking date+time strings
    // Format: "dddd, MMMM D, YYYY hh:mma" (e.g., "Tuesday, February 3, 2026 6:00am")
    const customFormats = [
      'dddd, MMMM D, YYYY h:mma',
      'dddd, MMMM D, YYYY hh:mma',
      'dddd, MMMM D, YYYY h:mm a',
      'dddd, MMMM D, YYYY hh:mm a',
      'MMMM D, YYYY h:mma',
      'MMMM D, YYYY hh:mma',
      'MMMM D, YYYY h:mm a',
      'MMMM D, YYYY hh:mm a',
      'dddd, MMMM D, YYYY HH:mm',
      'MMMM D, YYYY HH:mm',
      'YYYY-MM-DD HH:mm',
      'YYYY-MM-DD h:mm a',
    ];

    // Try parsing with custom formats first
    for (const format of customFormats) {
      const parsed = dayjs(dateTimeString, format);
      if (parsed.isValid()) {
        return parsed.toDate();
      }
    }

    // Try parsing with dayjs default
    const parsedDate = dayjs(dateTimeString);
    if (parsedDate.isValid()) {
      return parsedDate.toDate();
    }
    // Fallback to native Date parsing
    const formattedDateTimeNew = new Date(dateTimeString);
    return formattedDateTimeNew;
  }

  const offerPriceAsNumber = Number(offer?.offerPrice || 0);

  // Check if this is an instant booking (package or regular)
  const isInstantBooking = bookingData?.instantBook || bookingData?.packageBooking;

  const offeredDate = offer?.offeredDate && offer?.offeredTime
    ? formatDateTime(`${offer.offeredDate} ${offer.offeredTime}`)
    : (offer?.arivingDateTime ? formatDateTime(offer.arivingDateTime) : new Date());
  const amountDisplay =
    discount > 0
      ? parseFloat(offerPriceAsNumber) +
      calculateRenterFee(offerPriceAsNumber * 100, listingData) / 100 +
      parseFloat(platformPercentages.salesTax) *
      parseFloat(offerPriceAsNumber) -
      parseFloat(discount / 100).toLocaleString()
      : (
        parseFloat(offerPriceAsNumber) +
        calculateRenterFee(offerPriceAsNumber * 100, listingData) / 100 +
        parseFloat(platformPercentages.salesTax) *
        parseFloat(offerPriceAsNumber)
      ).toLocaleString();
  const amountInteger =
    discount > 0
      ? parseFloat(offerPriceAsNumber) +
      parseFloat(platformPercentages.salesTax) *
      parseFloat(offerPriceAsNumber) -
      parseFloat(discount / 100).toLocaleString()
      : parseFloat(offerPriceAsNumber) +
      parseFloat(platformPercentages.salesTax) *
      parseFloat(offerPriceAsNumber);
  // Convert the date string to a Date object
  // Calculate display amounts (in dollars)
  const platformFeeAmountDisplay =
    calculateRenterFee(offerPriceAsNumber * 100, listingData) / 100; // convert to cents for calc, back to dollars
  const taxRate = parseFloat(platformPercentages.salesTax);
  const taxAmountDisplay = taxRate * parseFloat(offerPriceAsNumber);
  const totalAmountDisplay =
    parseFloat(offerPriceAsNumber) +
    platformFeeAmountDisplay +
    taxAmountDisplay;
  const passengerTotal = toPassengerCount(
    bookingData?.passengers?.total ?? bookingData?.passengers,
    "total"
  );

  // Charter deposit percentage logic
  // When a boat owner sets a deposit percentage (e.g. 10%), the renter pays only that % online
  // Balance depends on collectBalanceAtMeetup: true = cash at meetup, false = BBR collects 48hrs prior
  const charterDepositPct = listingData?.fishingGuideDetails?.policies?.paymentTerms?.depositPercentage
    ? parseFloat(listingData.fishingGuideDetails.policies.paymentTerms.depositPercentage)
    : null;
  const hasCharterDeposit = charterDepositPct !== null && charterDepositPct > 0 && charterDepositPct < 100;
  const hoursUntilTrip = getHoursUntilTrip(offer, bookingData);
  const useCharterDeposit = shouldUseCharterDeposit(hasCharterDeposit, hoursUntilTrip);
  const displayCollectAtMeetup = listingData?.fishingGuideDetails?.policies?.paymentTerms?.collectBalanceAtMeetup === true;
  // Deposit % applies to base+tax only; renter fee is always added on top
  const baseAndTaxDisplay = parseFloat(offerPriceAsNumber) + taxAmountDisplay;
  const charterDepositAmountDisplay = useCharterDeposit
    ? (charterDepositPct / 100) * baseAndTaxDisplay + platformFeeAmountDisplay
    : totalAmountDisplay;
  const charterCashBalanceDisplay = useCharterDeposit
    ? totalAmountDisplay - charterDepositAmountDisplay
    : 0;

  const currentDate = new Date();
  const difference = offeredDate - currentDate;
  const differenceInDays = isNaN(difference)
    ? 0
    : difference / (1000 * 60 * 60 * 24);

  const discountAmountDisplay = discount > 0 ? discount / 100 : 0;
  const isAdvanceDepositDisplay =
    !useCharterDeposit &&
    !isInstantBooking &&
    differenceInDays > 14 &&
    offerPriceAsNumber >= 200;
  const advanceDepositRentalDisplay = 0.2 * offerPriceAsNumber;
  const advanceDepositTotalDisplay =
    advanceDepositRentalDisplay +
    platformFeeAmountDisplay +
    taxAmountDisplay -
    discountAmountDisplay;
  const remainingRentalDisplay = 0.8 * offerPriceAsNumber;

  // Console logs for display amounts (in dollars)
  try {
    console.log("PaymentForm display amounts", {
      baseAmount: Number(offerPriceAsNumber),
      platformFee: Number(platformFeeAmountDisplay.toFixed(2)),
      tax: Number(taxAmountDisplay.toFixed(2)),
      total: Number(totalAmountDisplay.toFixed(2)),
      charterDepositPct: charterDepositPct,
      charterDepositAmount: useCharterDeposit ? Number(charterDepositAmountDisplay.toFixed(2)) : null,
      charterCashBalance: useCharterDeposit ? Number(charterCashBalanceDisplay.toFixed(2)) : null,
      hoursUntilTrip: Number.isFinite(hoursUntilTrip) ? hoursUntilTrip : null,
      useCharterDeposit,
    });
  } catch (e) {
    console.warn("PaymentForm display amount logging failed", e);
  }

  // Check if offerPriceAsNumber is a valid number
  const formattedOfferPrice = isNaN(offerPriceAsNumber)
    ? "Invalid Number"
    : offerPriceAsNumber.toLocaleString();

  const handleCloseModal = () => {
    setShowPaymentModal(false);
  };

  // If deadline has expired, show error and block payment
  // Also disable navigation blocking since we're showing an error state
  useEffect(() => {
    if (deadlineExpired) {
      setShouldBlockNavigation(false);
    }
  }, [deadlineExpired]);

  if (deadlineExpired) {
    return (
      <div className="row">
        <div className="col">
          <div className="card p-4">
            <Alert severity="error" className="mb-3">
              {deadlineMessage}
            </Alert>
            <Link to="/dashboard/bookings" className="btn btn-primary">
              View My Bookings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Payment confirmation modal */}
      <Modal show={showPaymentModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>You&apos;ve accepted the offer!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            Complete payment to lock-in your booking.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseModal}>
            Got it, let&apos;s pay
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Navigation blocker modal - shown when trying to navigate via browser back/forward */}
      <Modal show={showNavigationModal} onHide={() => setShowNavigationModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pending Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have a pending payment. You can complete this payment within 24 hours, otherwise the booking will be cancelled. Do you want to leave this page?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowNavigationModal(false);
            setPendingNavigation(null);
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => {
            setShouldBlockNavigation(false);
            setShowNavigationModal(false);
            if (pendingNavigation) {
              setTimeout(() => navigate(pendingNavigation), 0);
            }
          }}>
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="row">
        <div className="col">
          <div className="page-description d-flex justify-content-between">
            <h1>{bookingData.listingTitle}</h1>
          </div>
        </div>
      </div>

      {/* Payment deadline warning */}
      {deadlineMessage && !deadlineExpired && (
        <Alert severity="warning" className="mb-3">
          {deadlineMessage}
        </Alert>
      )}

      {/* Booking Info Header */}
      <div className="card p-3 mb-3">
        <div className="d-flex flex-wrap align-items-center gap-3">
          <div>
            <small className="text-muted d-block">Charter</small>
            <strong>{bookingData.listingTitle || 'N/A'}</strong>
          </div>
          {bookingData?.packageBooking && bookingData?.packageDetails?.name && (
            <div>
              <small className="text-muted d-block">Package</small>
              <strong>{bookingData.packageDetails.name}</strong>
            </div>
          )}
          {passengerTotal > 0 && (
            <div>
              <small className="text-muted d-block">Passengers</small>
              <strong>{passengerTotal}</strong>
            </div>
          )}
          {(offer?.duration || bookingData?.packageDetails?.duration) && (
            <div>
              <small className="text-muted d-block">Duration</small>
              <strong>{offer?.duration || bookingData?.packageDetails?.duration}</strong>
            </div>
          )}
          <div>
            <small className="text-muted d-block">Trip Date</small>
            <strong>{bookingData?.tripDate || offer?.offeredDate || 'N/A'}</strong>
          </div>
        </div>
      </div>

      <Row className="card p-4">
        <div className="mb-4">
          <div className="mt-2">
            <div className="row">
              <div className="col-sm-4">
                <p className="mb-0">Amount</p>
              </div>
              <div className="col-sm-8">
                {/* Charter deposit: renter pays only deposit % online */}
                {useCharterDeposit ? (
                  <h3 className="text-muted mb-0 text-dark">
                    ${charterDepositAmountDisplay.toFixed(2)}{" "}
                    <span
                      className="text-muted mb-0"
                      style={{ fontSize: "14px" }}
                    >
                      ({charterDepositPct}% deposit of ${baseAndTaxDisplay.toFixed(2)} + ${platformFeeAmountDisplay.toFixed(2)} platform fee = ${charterDepositAmountDisplay.toFixed(2)} due now, out of ${totalAmountDisplay.toFixed(2)} total).{" "}
                      {displayCollectAtMeetup
                        ? `Remaining $${charterCashBalanceDisplay.toFixed(2)} to be collected by boat owner at meet-up.`
                        : `Remaining $${charterCashBalanceDisplay.toFixed(2)} will be collected by BBR 48 hours prior to the trip.`}
                    </span>
                  </h3>
                ) : isInstantBooking || differenceInDays <= 14 ||
                  (differenceInDays > 14 && offerPriceAsNumber < 200) ||
                  (hasCharterDeposit && !useCharterDeposit) ? (
                  // Instant booking OR near-term booking OR charter trip within 48 hours: full payment upfront
                  <h3 className="text-muted mb-0 text-dark">
                    ${amountDisplay}{" "}
                    <span
                      className="text-muted mb-0"
                      style={{ fontSize: "14px" }}
                    >
                      {isInstantBooking
                        ? `(full payment for instant booking${platformFeeAmountDisplay > 0 ? ", includes 5% platform fees" : ""}${isTaxable ? " and 8.25% tax" : ""}${platformFeeAmountDisplay === 0 && !isTaxable ? ", no platform fees or tax applicable" : ""})`
                        : hasCharterDeposit && !useCharterDeposit
                          ? `(full payment required — trip is within 48 hours${platformFeeAmountDisplay > 0 ? ", includes 5% platform fees" : ""}${isTaxable ? (platformFeeAmountDisplay > 0 ? " and 8.25% tax" : ", includes 8.25% tax") : ""})`
                        : platformFeeAmountDisplay > 0 || isTaxable
                          ? `(${platformFeeAmountDisplay > 0 ? "includes 5% platform fees" : ""}${isTaxable ? (platformFeeAmountDisplay > 0 ? " and 8.25% tax" : "includes 8.25% tax") : ""})`
                          : "(no platform fees or tax applicable)"}
                    </span>
                  </h3>
                ) : isAdvanceDepositDisplay ? (
                  // Advance booking with deposit + full fees/tax upfront
                  <>
                    <h3 className="text-muted mb-0 text-dark">
                      ${advanceDepositTotalDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                      <span
                        className="text-muted mb-0"
                        style={{ fontSize: "14px" }}
                      >
                        deposit (20% of rental plus full platform fees
                        {isTaxable ? " and tax" : ""}). Remaining ${remainingRentalDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                        (remaining rental balance) will be charged 48 hours prior
                        to start date.
                      </span>
                    </h3>
                  </>
                ) : null}
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-4">
                <p className="mb-0">Offered Date Time</p>
              </div>
              <div className="col-sm-8">
                <p clasName="text-muted mb-0">
                  {offeredDate.toLocaleString("en-US", options)}
                </p>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-4">
                <p className="mb-0">Duration</p>
              </div>
              <div className="col-sm-8">
                <p className="text-muted mb-0">{offer?.duration || bookingData?.packageDetails?.duration || 'N/A'}</p>
              </div>
            </div>
            <hr />
            <div className="row">
              <div className="col-sm-4">
                <p className="mb-0">Arriving Date Time</p>
              </div>
              <div className="col-sm-8">
                <p className="text-muted mb-0">{offer?.arivingDateTime || 'N/A'}</p>
              </div>
            </div>
            {/* First-time owner booking notice */}
            {totalOwnerBookings === 0 && offerPriceAsNumber < 200 && (
              <>
                <hr />
                <div className="row">
                  <div className="col-sm-4">
                    <p className="mb-0">First-Time Owner</p>
                  </div>
                  <div className="col-sm-8">
                    <p className="text-muted mb-0">
                      <span className="text-info">
                        Note: Special pricing applies for this owner's first
                        booking.
                      </span>
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <Col>
          <form onSubmit={handleSubmit} className="mt-4">
            <div style={{ marginBottom: "20px" }}>
              <Row>
                <Col>
                  <TextField
                    id="standard-basic"
                    value={coupon}
                    onChange={(e) => setCouponCode(e.target.value)}
                    label="Coupon Code"
                    variant="standard"
                  />
                  <Button
                    variant="primary"
                    onClick={() => verifyDiscount(coupon)}
                    className="btn btn-primary m-1"
                  >
                    Apply
                  </Button>
                  {discount > 0 ? (
                    <Alert className="mt-3" severity="success">
                      -${discount / 100} discount has been applied.
                    </Alert>
                  ) : (
                    ""
                  )}
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <CardElement
                    className="form-control"
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": {
                            color: "#aab7c4",
                          },
                        },
                        invalid: {
                          color: "#9e2146",
                        },
                      },
                    }}
                  />
                  {error && (
                    <Alert className="mt-3" severity="error">
                      {`${error}` ?? ""}
                    </Alert>
                  )}
                </Col>
              </Row>
              {listingData?.boatType === "Bass Boat" && (
                <Row className="mt-4">
                  <Col>
                    <div className="d-flex mt-1 additional-box">
                      <FormControlLabel
                        style={{ alignItems: "flex-start" }}
                        control={<Checkbox checked={acknowledgementBassBoat} />}
                        onChange={() => setAcknowledgementBassBoat(!acknowledgementBassBoat)}
                      />
                      <div>
                        <p style={{ fontWeight: "500", lineHeight: "24px", margin: 0 }}>
                          I understand bass boats come with 1 hour of motor use per 24 hour rental period. Additional motor hours used could be charged at the discretion of the boat owner at $50 per hour.
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="mt-4">
                <Col>
                  <div className="d-flex mt-1 additional-box">
                    <FormControlLabel
                      style={{ alignItems: "flex-start" }}
                      control={<Checkbox checked={acknowledgement2} />}
                      onChange={() => setAcknowledgement2(!acknowledgement2)}
                    />
                    <div>
                      <p style={{ fontWeight: "500", lineHeight: "28px" }}>
                        Please check this box to acknowledge that you have read
                        and agreed to the cancellation policy below
                      </p>
                      {(() => {
                        const cancellationPolicy = listingData?.fishingGuideDetails?.policies?.cancellation;
                        const isFreeCancellationPackage = bookingData?.packageBooking && bookingData?.packageDetails?.name && 
                          listingData?.fishingGuideDetails?.tripPackages?.find(p => p.name === bookingData.packageDetails.name)?.freeCancellation;
                        if (isFreeCancellationPackage) {
                          return (
                            <div className="p-2 mt-1" style={{ backgroundColor: '#e8f5e9', borderRadius: '6px', fontSize: '13px' }}>
                              <strong>Free Cancellation</strong> — 100% refund for all cancellations. No charges will be applied.
                            </div>
                          );
                        }
                        if (cancellationPolicy === 'Relaxed') {
                          return (
                            <div className="p-2 mt-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '13px' }}>
                              <strong>Relaxed Policy:</strong><br/>
                              • <strong>100%</strong> refund if cancelled more than <strong>24 hours</strong> before the trip.<br/>
                              • <strong>50%</strong> refund if cancelled within <strong>24 hours</strong> of the trip.
                            </div>
                          );
                        }
                        if (cancellationPolicy === 'Regulated') {
                          return (
                            <div className="p-2 mt-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '13px' }}>
                              <strong>Regulated Policy:</strong><br/>
                              • <strong>100%</strong> refund if cancelled more than <strong>7 days</strong> before the trip.<br/>
                              • <strong>75%</strong> refund if cancelled less than <strong>7 days</strong> but more than <strong>48 hours</strong> before the trip.<br/>
                              • <strong>50%</strong> refund if cancelled less than <strong>48 hours</strong> but more than <strong>24 hours</strong> before the trip.
                            </div>
                          );
                        }
                        if (cancellationPolicy === 'Restricted') {
                          return (
                            <div className="p-2 mt-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '13px' }}>
                              <strong>Restricted Policy:</strong><br/>
                              • <strong>75%</strong> refund if cancelled more than <strong>7 days</strong> before the trip.<br/>
                              • <strong>50%</strong> refund if cancelled less than <strong>7 days</strong> but more than <strong>48 hours</strong> before the trip.<br/>
                              • <strong>25%</strong> refund if cancelled less than <strong>48 hours</strong> but more than <strong>24 hours</strong> before the trip.
                            </div>
                          );
                        }
                        if (cancellationPolicy === 'Free') {
                          return (
                            <div className="p-2 mt-1" style={{ backgroundColor: '#e8f5e9', borderRadius: '6px', fontSize: '13px' }}>
                              <strong>Free Cancellation:</strong> 100% refund for all cancellations. No charges will be applied.
                            </div>
                          );
                        }
                        return (
                          <div className="p-2 mt-1" style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', fontSize: '13px' }}>
                            <strong>Standard BBR Policy:</strong><br/>
                            • <strong>Full refund</strong> if cancelled at least <strong>2 weeks</strong> before the trip.<br/>
                            • <strong>20% deposit</strong> forfeited if cancelled <strong>2 weeks to 48 hours</strong> before the trip.<br/>
                            • <strong>No refund</strong> if cancelled within <strong>48 hours</strong> of the trip.<br/>
                            Refer to the <Link to="/cancellation">BBR cancellation policy</Link> for additional details.
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            <button
              variant="primary"
              type="submit"
              className="btn btn-primary m-1"
              disabled={isPayButtonDisabled}
            >
              {loading ? (
                <ThreeDots
                  visible={true}
                  height="22"
                  width="22"
                  color="#fff"
                  radius="20"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                />
              ) : (
                "Pay"
              )}
            </button>
          </form>
        </Col>
      </Row>
    </>
  );
};

export default PaymentForm;
